import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { VacationWidget } from '@com-delta-idp/mnx-sitewide-header-ui';
import {
  HeaderFooterMachCore,
  HeaderFooterMachCoreContentService,
  LoggingService,
  SearchOrgDestRequest,
  SearchOrgDestResponse,
  VacationContentService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { DestinationType } from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { PACKAGE_TYPE } from '@com-delta-idp/mnx-sitewide-shared-utils';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, concatMap, map, take } from 'rxjs/operators';
import { BookingVacationAdapterService } from '../../adapters/booking-vacation/booking-vacation-adapter.service';
import { DLV } from '../../constants/booking-vacation/booking-vacation.constants';
import { BookingVacationFormService } from '../../utils/booking-vacation/booking-vacation-form.service';
import { BookingVacationCertificatesService } from './booking-vacation-certificates.service';

@Injectable({ providedIn: 'root' })
export class BookingVacationFacadeBaseService {
  adapter = inject(BookingVacationAdapterService);
  vacationService = inject(VacationContentService);
  formService = inject(BookingVacationFormService);
  content = inject(HeaderFooterMachCoreContentService);
  loggingService = inject(LoggingService);
  certificatesService = inject(BookingVacationCertificatesService);
  vacationWidgetContent$ = new BehaviorSubject<VacationWidget>(
    {} as VacationWidget
  );

  vacationWidgetForm!: FormGroup;

  destinationType$ = new BehaviorSubject<DestinationType>('');

  init(callback?: (success: boolean) => void): void {
    const searchReqOrigin: SearchOrgDestRequest = {
      brandCode: DLV,
      lifestyleCode: '',
      isOrigin: true,
      query: '',
      pkgType: PACKAGE_TYPE.AIR_HOTEL
    };
    const searchReqDestination: SearchOrgDestRequest = {
      brandCode: DLV,
      lifestyleCode: '',
      isOrigin: false,
      query: '',
      pkgType: PACKAGE_TYPE.AIR_HOTEL
    };

    this.vacationWidgetForm = this.formService.createBookingForm();

    this.content
      .getHeaderFooterMachCoreContent$()
      .pipe(
        concatMap((aem: HeaderFooterMachCore) => {
          return this.vacationService
            .getVacationOriginAndDestination(
              searchReqOrigin,
              searchReqDestination
            )
            .pipe(
              map(
                (
                  airportListContent: [
                    SearchOrgDestResponse,
                    SearchOrgDestResponse
                  ]
                ) => {
                  return { aem, airportListContent };
                }
              ),
              catchError((error: HttpErrorResponse) => {
                const errorMessage = `Vacation API failed: ${error.message}`;
                this.loggingService.log(errorMessage);
                return of({ aem, airportListContent: null });
              })
            );
        }),
        take(1),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = `AEM API failed: ${error.message}`;
          this.loggingService.log(errorMessage);
          return of({ aem: null, airportListContent: null });
        })
      )
      .subscribe((result) => this.handleInitResult(result, callback));
  }

  protected handleInitResult(
    {
      aem,
      airportListContent
    }: {
      aem: HeaderFooterMachCore | null;
      airportListContent: [SearchOrgDestResponse, SearchOrgDestResponse] | null;
    },
    callback?: (success: boolean) => void
  ): void {
    const success = aem !== null && airportListContent !== null;
    if (aem) {
      const widgetContent = this.adapter.getContent(aem);
      this.certificatesService.resolveCertificatesLinkUrl(
        widgetContent,
        aem,
        this.vacationWidgetContent$
      );
    }
    if (airportListContent)
      this.adapter.setAirportListArray(airportListContent);
    callback?.(success);
  }
}
